"use client"

import React from "react"
import Autocomplete from "@mui/material/Autocomplete"
import { styled } from "@mui/material/styles"
import TextField from "@mui/material/TextField"
import clsx from "clsx"
import { ControllerRenderProps } from "react-hook-form"
import { Absolute } from "ui/components/atoms/_atoms"
import Icon from "ui/components/atoms/icon"
import Spinner from "ui/components/atoms/spinner"
import { Label } from "ui/components/atoms/typography"
import { baseTheme } from "ui/theme"
import { BaseStyles } from "ui/types/base-styles"
import { generateStylesFromProps } from "ui/utils/styles"

import { ErrorMessage } from "./Input"
import styles from "./Input.module.css"

const StyledTextField = styled(TextField)(() => {
  return {
    "width": "100%",
    "background": "none",
    ".MuiInputBase-root": {
      "boxSizing": "border-box",
      "background": "none",
      "paddingLeft": baseTheme.font.XS,
      "paddingTop": baseTheme.font.XS,
      "paddingBottom": baseTheme.font.XS,
      "borderRadius": baseTheme.variable.borderRadius,

      "&:before, &:after": {
        display: "none",
      },

      "input, textarea": {
        boxSizing: "border-box",
        color: baseTheme.color.second,
        fontSize: baseTheme.font.L,
        lineHeight: baseTheme.font.XL,
        fontWeight: baseTheme.font.light,
        padding: "0",
        paddingRight: "10px",
      },
      "input": {
        "height": 25,

        "&::placeholder": {
          color: `${baseTheme.color.third} !important`,
          opacity: 1,
        },
      },
    },
    ".MuiInputAdornment-root": {
      margin: "0 !important",
      fontSize: baseTheme.font.L,
      lineHeight: baseTheme.font.XL,
      fontWeight: baseTheme.font.light,

      p: {
        fontSize: baseTheme.font.L,
        lineHeight: baseTheme.font.XL,
        fontWeight: baseTheme.font.light,
      },
    },
  }
})

export const InputAutocomplete = ({
  name,
  label,
  icon,
  inputValue,
  error,
  placeholder,
  loading,
  options,
  onChange,
  onInputChange = () => {},
  onOpen,
  noOptionsText = "No options",
  $background = "none",
  $borderColor,
  ...rest
}: ControllerRenderProps &
  BaseStyles & {
    name?: string
    label?: string
    icon?: string
    placeholder?: string
    loading?: boolean
    inputValue?: string
    onOpen?: () => void
    onInputChange?: (e: any) => void
    error?: {
      type?: string
      message?: string
    }
    noOptionsText?: string
    options: {
      id: string
      label: string
    }[]
  }) => {
  const generatedStyles = generateStylesFromProps({ ...rest })
  const inputStyled = generateStylesFromProps({ $borderColor, $background })

  return (
    <Autocomplete
      options={options}
      onChange={(_, data) => onChange(data)}
      noOptionsText={noOptionsText}
      inputValue={inputValue}
      onOpen={onOpen}
      onInputChange={(e, value, reason) => {
        if (e) {
          onInputChange(value)
        }
      }}
      defaultValue={null}
      filterOptions={x => x}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={({ InputProps, ...params }) => {
        const boxClasses = clsx(styles.styledBox, {
          [styles.withIcon]: !!icon,
        })

        return (
          <div data-name={name} style={generatedStyles}>
            {label && (
              <Label $type="secondary" $marginBottom="XS">
                {label}
              </Label>
            )}
            <div className={boxClasses}>
              <StyledTextField
                placeholder={placeholder}
                variant="outlined"
                InputProps={{
                  ...InputProps,
                  style: inputStyled,
                }}
                {...params}
              />
              {loading && (
                <div className={styles.iconWrapper}>
                  <Spinner $size="15px" $borderWidth={3} />
                </div>
              )}
              {icon && (
                <Absolute
                  $left={baseTheme.font.S}
                  $top="50%"
                  style={{ transform: "translateY(-50%)" }}
                  $display="flex"
                  $alignItems="center"
                >
                  <Icon $size="15px" icon={icon} />
                </Absolute>
              )}
            </div>
            {error && <ErrorMessage>{error?.message ?? "To pole jest wymagane"}</ErrorMessage>}
          </div>
        )
      }}
    />
  )
}
